import globalAgent from 'src/agent';

export type AcquisitionMode = 'event' | 'cohort';
export type AcquisitionSourceStatus = 'complete' | 'partial' | 'pending' | 'error' | 'not_connected';
export type AcquisitionProduct =
    | 'lead_package'
    | 'lead_year_unlimited'
    | 'user_subscription'
    | 'project_subscription'
    | 'agent_dialog_package'
    | 'agent_wallet_topup';

export interface AcquisitionFilters {
    dateFrom: string;
    dateToInclusive: string;
    mode: AcquisitionMode;
    product?: AcquisitionProduct | '';
    channel?: string;
    campaign?: string;
}

export interface AcquisitionCoverage {
    status: AcquisitionSourceStatus;
    covered_from: string | null;
    covered_to: string | null;
    fetched_at: string | null;
    reason: string;
    connected?: string[];
    unsupported_or_unknown?: string[];
}

export interface AcquisitionStep {
    key: string;
    events: number | null;
    unique_entities: number | null;
    reason: string | null;
}

export interface AcquisitionJourneyPath {
    status: string;
    reason: string | null;
    eligible_journeys: number | null;
    steps: AcquisitionStep[];
}

export interface AcquisitionMoneyRow {
    currency: string;
    purchases: {
        operations: number;
        buyers: number;
        buyers_complete: boolean;
        nominal_value: string | null;
        paid_value: string | null;
        wallet_value: string | null;
        bonus_value: string | null;
        amounts_complete: boolean;
    };
    cash_in: {
        operations: number;
        external_cash_value: string | null;
    };
    refunds: {
        observed_operations: number;
        observed_value: string | null;
        net_value: string | null;
        net_reason: string | null;
    };
}

export interface AcquisitionReport {
    schema_version: 2;
    request: {
        mode: AcquisitionMode;
        date_from: string;
        date_to: string;
        date_to_exclusive: true;
        timezone: 'Europe/Moscow';
        start_utc: string;
        end_utc: string;
        observation_end: string;
        cohort_definition: string;
        filters: {
            product: AcquisitionProduct | null;
            channel: string | null;
            campaign: string | null;
        };
    };
    available_filters: {
        products: Array<{
            value: AcquisitionProduct;
            source_status: AcquisitionSourceStatus;
            applies_to: 'canonical_orders_and_financial_outcomes' | 'external_cash_only' | 'not_connected';
        }>;
        channel: {
            supported: boolean;
            event_financial_match: string;
            event_nonfinancial_match: string;
            cohort_match: string;
            cross_channel_totals_additive: false;
        };
        campaign: {
            supported: boolean;
            event_financial_match: string;
            event_nonfinancial_match: string;
            cohort_match: string;
            cross_campaign_totals_additive: false;
        };
        browser_surface: { supported: boolean; financial_product_mapping: false };
    };
    coverage: Record<string, AcquisitionCoverage>;
    observed_steps: AcquisitionStep[];
    browser_observations: {
        surface_mapping: string;
        unique_entity_definition: string;
        rows: Array<{
            event_type: 'product_viewed' | 'registration_clicked' | 'registration_started';
            surface: string;
            events: number;
            unique_entities: number;
        }>;
        reason: string | null;
    };
    linked_journey: {
        observation_end: string;
        unique_entity_definition: string;
        maturity_window: { days: number | null; status: string; reason: string };
        immature_journeys: number | null;
        immature_reason: string | null;
        paths: {
            new_account: AcquisitionJourneyPath;
            returning_buyer: AcquisitionJourneyPath;
        };
        order_purchase: {
            linked: number;
            total_purchases: number;
            coverage: string | null;
            observed_live_orders: number;
            comparable_live_orders: number;
            orders_without_linked_purchase: number;
            free_orders: number;
            legacy_purchases_without_order: number;
            environment_mismatches: number;
            test_orders_excluded: number;
            ambiguous_matches: number;
            conflicting_matches: number;
            temporal_mismatches: number;
            linkage: string;
        };
        buyer_history: {
            known_returning_orders: number;
            first_observed_orders: number;
            reason: string;
        };
    };
    money: {
        currencies: AcquisitionMoneyRow[];
        cross_currency_total: null;
        cross_currency_reason: string;
    };
    products: Array<AcquisitionMoneyRow & { product: AcquisitionProduct }>;
    metrica: {
        counter_id: number;
        model: string | null;
        model_reason: string | null;
        sampling: 'unknown' | 'sampled' | 'unsampled' | 'mixed';
        visits: number | null;
        visits_reason: string | null;
        period_unique_visitors: null;
        period_unique_reason: string;
        missing_days: string[];
        daily_rows: Array<{
            date: string;
            model: string;
            visits: number | null;
            daily_unique_visitors: number | null;
            sampled: boolean | null;
            completeness: string;
            fetched_at: string | null;
        }>;
    };
    direct_cost_details?: {
        value: string | null;
        currency: string | null;
        reason: string | null;
        status: AcquisitionSourceStatus;
        period_kind: 'calendar_event_period';
        period_from: string;
        period_to_exclusive: string;
        observed_campaigns: number | null;
        selected_campaigns: number | null;
        mapping_status: string;
        provenance: {
            source: string;
            snapshot_fingerprint: string;
            collected_at: string;
            direct_period_to_inclusive: string;
            money_units: string;
            include_vat: boolean;
            include_discount: boolean;
            return_money_in_micros: boolean;
        } | null;
    };
    economics: {
        direct_cost: string | null;
        direct_cost_currency: string | null;
        direct_cost_reason: string;
        cpa: string | null;
        cpa_reason: string;
        cac: string | null;
        cac_reason: string;
        roas: string | null;
        roas_reason: string;
        roi: string | null;
        roi_reason: string;
    };
}

export type AcquisitionJourneyProduct = 'leadsmania' | 'tghunter' | 'unknown';

export interface AcquisitionJourneyStep {
    status: 'observed' | 'unknown';
    occurred_at: string | null;
    evidence: string;
}

export interface AcquisitionJourneyRow {
    account_ref: string;
    product: AcquisitionJourneyProduct;
    campaign: { status: 'observed' | 'unknown'; campaign: string | null; ad: string | null; evidence: string };
    binding: { status: 'observed' | 'unknown'; evidence: string };
    registration: AcquisitionJourneyStep & { new_account: boolean | null };
    confirmation: AcquisitionJourneyStep;
    setup: AcquisitionJourneyStep;
    orders: Array<{ occurred_at: string; product: string }>;
    payments: {
        provider_verified: Array<{ occurred_at: string; product: string; amount: string | null; currency: string }>;
        native_paid_unverified: Array<{ occurred_at: string; product: string; amount: string; currency: string }>;
        coverage: 'partial' | 'unknown';
    };
}

export interface AcquisitionJourneyReport {
    count: number;
    count_is_exact: boolean;
    candidate_limit: number;
    as_of: string;
    limit: number;
    offset: number;
    results: AcquisitionJourneyRow[];
}

const CAPABILITIES_URL = '/acquisition/staff/capabilities/';
const REPORT_URL = '/acquisition/staff/report/';
const JOURNEYS_URL = '/acquisition/staff/journeys/';

export function inclusiveDateToExclusive(value: string): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Некорректная дата окончания');
    const date = new Date(`${value}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
        throw new Error('Некорректная дата окончания');
    }
    date.setUTCDate(date.getUTCDate() + 1);
    return date.toISOString().slice(0, 10);
}

export function buildReportParams(filters: AcquisitionFilters) {
    const dateTo = inclusiveDateToExclusive(filters.dateToInclusive);
    const start = new Date(`${filters.dateFrom}T00:00:00.000Z`);
    const end = new Date(`${dateTo}T00:00:00.000Z`);
    if (
        !/^\d{4}-\d{2}-\d{2}$/.test(filters.dateFrom) ||
        Number.isNaN(start.getTime()) ||
        start.toISOString().slice(0, 10) !== filters.dateFrom ||
        end <= start
    ) {
        throw new Error('Проверьте границы периода');
    }
    const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
    if (days > 366) throw new Error('Период не может быть длиннее 366 дней');
    return {
        date_from: filters.dateFrom,
        date_to: dateTo,
        mode: filters.mode,
        ...(filters.product ? { product: filters.product } : {}),
        ...(filters.channel?.trim() ? { channel: filters.channel.trim() } : {}),
        ...(filters.campaign?.trim() ? { campaign: filters.campaign.trim() } : {}),
    };
}

export function buildJourneyParams(filters: AcquisitionFilters) {
    const report = buildReportParams(filters);
    const product = filters.product === 'project_subscription'
        ? 'tghunter'
        : filters.product === 'user_subscription'
            ? 'unknown'
            : filters.product
            ? 'leadsmania'
            : undefined;
    return {
        date_from: report.date_from,
        date_to: report.date_to,
        ...(product ? { product } : {}),
        ...(filters.campaign?.trim() ? { campaign: filters.campaign.trim() } : {}),
        limit: 50,
        offset: 0,
    };
}

class AcquisitionAnalyticsService {
    async getCapabilities(): Promise<{ own_analytics: boolean; journeys: boolean }> {
        const response = await globalAgent.get(CAPABILITIES_URL);
        return response.data;
    }

    async getReport(filters: AcquisitionFilters): Promise<AcquisitionReport> {
        const response = await globalAgent.get(REPORT_URL, { params: buildReportParams(filters) });
        return response.data;
    }

    async getJourneys(filters: AcquisitionFilters): Promise<AcquisitionJourneyReport> {
        const response = await globalAgent.get(JOURNEYS_URL, { params: buildJourneyParams(filters) });
        return response.data;
    }
}

export default new AcquisitionAnalyticsService();
